import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Params, Router } from '@angular/router'
import { Checkout, Depot, User, UserGroup } from 'depoto-core/src/entities'
import { HistoryUtil, SchemaUtil, showCookieConsentDialog } from '../../utils'
import { CoreService, NotificationService } from '../../services'
import { AbstractControl, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms'
import { CheckDataChanges } from '../../services/can-deactivate-guard.service'
import { Institution } from 'depoto-core/src/entities/Institution'
import { TranslateService } from '@ngx-translate/core'
import * as Sentry from '@sentry/angular-ivy'
import { AVAILABLE_LANGUAGES } from '../../configs'

@Component({
  selector: 'app-user',
  templateUrl: './user.component.html',
  styles: [
    `
      .form-check-input.fix-checkbox-align {
        margin-top: 0.45rem;
      }
    `,
  ],
})
export class UserComponent implements OnInit, CheckDataChanges {
  user: User
  loggedInUser: User
  checkouts: Checkout[] = []
  depots: Depot[] = []
  groups: UserGroup[] = []
  isCreating = false
  isUserProfile = false
  loading = true
  submitted = false
  institutions: Institution[] = []
  isUserAdmin
  isPasswordValid = false
  password = ''

  public userForm = new UntypedFormGroup({
    email: new UntypedFormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
    // plainPassword: new UntypedFormControl({ value: '', disabled: false }, []),
    firstName: new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
    lastName: new UntypedFormControl({ value: '', disabled: false }, [Validators.required]),
    phone: new UntypedFormControl({ value: '', disabled: false }, []),
    enabled: new UntypedFormControl({ value: true, disabled: false }, []),
    locale: new UntypedFormControl({ value: '', disabled: false }, []),
  })

  public readonly availableLanguages = AVAILABLE_LANGUAGES

  constructor(
    private route: ActivatedRoute,
    public router: Router,
    private core: CoreService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
  ) {}

  ngOnInit() {
    this.isUserAdmin = this.core.services.user.hasRole('ROLE_USER_ADMIN_CREATE')
    this.route.params.forEach(async (params: Params) => {
      const id: number = +params['id']
      const profile: string = params['profile']
      await this.getInstitutions()
      await this.getCheckouts()
      this.depots = this.core.lists.depots
      if (!id) {
        if (!profile) {
          this.isCreating = true
          this.user = new User()
          // this.userForm.controls.plainPassword.setValidators([Validators.required])
          // this.userForm.controls.plainPassword.updateValueAndValidity()
          this.loading = false
          await this.getUserGroups()
        } else if (profile === 'profile') {
          this.isUserProfile = true
          // this.userForm.controls.plainPassword.clearValidators()
          // this.userForm.controls.plainPassword.updateValueAndValidity()
          await this.setCurrentUser()
          this.fillFormValue()
        }
      } else {
        // this.userForm.controls.plainPassword.clearValidators()
        // this.userForm.controls.plainPassword.updateValueAndValidity()
        await this.refreshData(id)
        this.fillFormValue()
      }
      this.loggedInUser = this.core.services.user.user
    })
  }

  fillFormValue() {
    this.userForm.setValue({
      email: this.user.email,
      // plainPassword: this.user.plainPassword,
      firstName: this.user.firstName,
      lastName: this.user.lastName,
      phone: this.user.phone,
      enabled: this.user.enabled,
      locale: this.user.locale,
    })
  }

  getFormValue() {
    this.user.email = this.userForm.value.email
    // this.user.plainPassword = this.userForm.value.plainPassword
    this.user.plainPassword = this.password
    this.user.firstName = this.userForm.value.firstName
    this.user.lastName = this.userForm.value.lastName
    this.user.phone = this.userForm.value.phone
    this.user.enabled = this.userForm.value.enabled
    this.user.locale = this.userForm.value.locale
  }

  hasUnsavedDataChanges(): boolean {
    return false
  }

  async refreshData(id: number) {
    this.loading = true
    this.user = new User(await this.core.services.user.getById(id, SchemaUtil.userProfile))
    await this.getCheckouts()
    await this.getUserGroups()
    this.loggedInUser = this.core.services.user.user
    this.loading = false
  }

  async getUserGroups() {
    this.loading = true
    this.groups = []
    const receivedData = await this.core.services.userGroup
      .getList(
        {},
        {
          id: null,
          name: null,
          externalId: null,
          roles: null,
        },
      )
      .then(result => result?.items || [])
      .catch(() => [])
    receivedData.forEach(group => {
      this.groups.push(new UserGroup(group))
    })
    this.loading = false
  }

  async getInstitutions() {
    const result = await this.core.services.institution.getList(
      {},
      {
        id: null,
        name: null,
        checkouts: {
          id: null,
          name: null,
          depots: {
            id: null,
            name: null,
          },
        },
        depots: {
          id: null,
          name: null,
        },
      },
    )
    this.institutions = result?.items || []
  }

  async getCheckouts() {
    this.loading = true
    this.checkouts = []
    const res = await this.core.services.checkout
      .getList(
        {},
        {
          id: null,
          name: null,
          institution: {
            id: null,
            name: null,
          },
          depots: {
            id: null,
            name: null,
          },
        },
      )
      .then(result => result?.items || [])
      .catch(() => [])
    res.forEach(checkout => {
      this.checkouts.push(new Checkout(checkout))
    })
    this.loading = false
  }

  async createUser() {
    this.submitted = true
    if (this.userForm.invalid || !this.isPasswordValid) {
      return
    }
    try {
      this.loading = true
      this.getFormValue()
      const user: User = await this.core.services.user.create(this.user)
      this.notificationService.success(this.translateService.instant('alert.user.created'))
      if (user && user.id > 0) {
        this.isCreating = false
        this.user = user
        this.router.navigate(['/user', user.id])
      }
    } catch (err) {
      console.log(err)
    } finally {
      this.loading = false
    }
  }

  async updateUser() {
    this.submitted = true
    if (this.userForm.invalid || (!this.isUserProfile && this.password?.length && !this.isPasswordValid)) {
      return
    }
    try {
      this.loading = true
      this.getFormValue()
      this.user.checkouts = this.checkouts
      const user: User = await this.core.services.user.update(this.user, SchemaUtil.user)
      this.notificationService.success(this.translateService.instant('alert.user.updated'))
      if (user && user.id > 0) {
        if (this.isUserProfile) {
          this.setCurrentUser()
        } else {
          // this.core.services.user.user = user // nope, veronigga.. bad idea
        }
      }
    } catch (err) {
      console.log(err)
    } finally {
      this.loading = false
    }
  }

  async deleteUser() {
    if (this.core.services.user.user?.email === this.user.email) {
      alert(this.translateService.instant('user.delete-yourself'))
      return
    }
    const confirmation = confirm(this.translateService.instant('user.delete.confirmation'))
    if (confirmation) {
      try {
        await this.core.services.user.delete(this.user)
        this.notificationService.success(this.translateService.instant('alert.user.deleted'))
        HistoryUtil.navigateBack()
      } catch (err) {
        console.log(err)
      }
    }
  }

  updateGroup(group: UserGroup, value: boolean) {
    if (value) {
      this.user.groups.push(group)
    } else {
      this.user.groups.forEach((g: UserGroup, i: number) => {
        if (g.id === group.id) {
          this.user.groups.splice(i, 1)
        }
      })
    }
  }

  updateCheckout(checkout: Checkout, value: boolean) {
    if (value) {
      this.user.checkouts.push(checkout)
    } else {
      this.user.checkouts.forEach((c: Checkout, i: number) => {
        if (c.id === checkout.id) {
          this.user.checkouts.splice(i, 1)
        }
      })
    }
  }

  updateDepot(depot: Depot, value: boolean) {
    if (value) {
      this.user.depots.push(depot)
    } else {
      this.user.depots.forEach((d: Depot, i: number) => {
        if (d.id === depot.id) {
          this.user.depots.splice(i, 1)
        }
      })
    }
  }

  updateInstitution(institution, value) {
    if (value) {
      institution.depots.forEach(depot => {
        if (!this.user.depots.find(d => d.id === depot.id)) {
          this.user.depots.push(depot)
        }
      })
      institution.checkouts.forEach(checkout => {
        if (!this.user.checkouts.find(c => c.id === checkout.id)) {
          this.user.checkouts.push(checkout)
        }
      })
    } else {
      this.user.depots = this.user.depots.filter(depot => !institution.depots.some(d => d.id === depot.id))
      this.user.checkouts = this.user.checkouts.filter(
        checkout => !institution.checkouts.some(ch => ch.id === checkout.id),
      )
    }
  }

  isUserInGroup(user: User, group: UserGroup) {
    let result = false
    if (user?.groups?.length > 0) {
      user.groups.forEach((g: UserGroup) => {
        if (g.id === group.id) {
          result = true
        }
      })
    }
    return result
  }

  hasUserInstitution(user, institution): boolean {
    return (
      institution.depots.some(depot => this.hasUserDepot(user, depot)) ||
      institution.checkouts.some(checkout => this.hasUserCheckout(user, checkout))
    )
  }

  hasUserCheckout(user: User, checkout: Checkout): boolean {
    let result = false
    if (user?.checkouts?.length > 0) {
      user.checkouts.forEach((c: Checkout) => {
        if (c.id === checkout.id) {
          result = true
        }
      })
    }
    return result
  }

  hasUserDepot(user: User, depot: Depot) {
    let result = false
    if (user?.depots?.length > 0) {
      user.depots.forEach((d: Depot) => {
        if (d.id === depot.id) {
          result = true
        }
      })
    }
    return result
  }

  isUserPermitted(user?: User) {
    let result = false
    const u = user ? user : this.user
    u.groups.forEach((g: UserGroup) => {
      g.roles.forEach((r: string) => {
        if (r === 'ROLE_ADMIN' || r === 'ROLE_SUPER_ADMIN') {
          result = true
        }
      })
    })
    return result
  }

  async setCurrentUser() {
    this.loading = true
    const userProfile = await this.core.services.user.getMyProfile(SchemaUtil.userProfile)
    if (userProfile) {
      this.user = new User(userProfile)
      await this.core.services.user.setCurrentUser(this.user.email)
      if (this.isUserPermitted()) {
        await this.getUserGroups()
        await this.getCheckouts()
      }
    }
    this.translateService.use(this.user.locale)
    this.loading = false
  }

  get formControls(): { [key: string]: AbstractControl } {
    return this.userForm.controls
  }

  showCookieConsentDialog() {
    showCookieConsentDialog()
  }

  triggerBackBtn() {
    if (window.history.length > 1) {
      window.history.back()
    } else {
      this.router.navigate(['/users'])
    }
  }
}
